<!-- ============================================================
  CourseUsageTimeline — ไทม์ไลน์ "การใช้คอร์ส" (จอง / ย้ายวัน / ยกเลิก / เช็คชื่อ / ลงอีเวนต์)
  จาก /getCourseUsageTimeline/:courserefer · ใช้ใน dialog ประวัติของหน้าคอร์สลูกค้า + ประวัติคอร์ส
  คู่กับ EditHistoryTimeline (การแก้ตัวคอร์ส) — แยกส่วนกันเพราะการใช้มีเป็นสิบ ถ้ารวมเส้นเดียว
  การแก้ราคาครั้งเดียวจะจมหาย
  - ตัวกรอง "ใบที่ยังใช้อยู่" = เหตุการณ์ของใบจองที่ยังไม่ถูกยกเลิก (แทนการวางตารางซ้ำ)
  - ไม่แสดง "หัก/คืนกี่ครั้ง" — backend ไม่รู้ย้อนหลังว่าคืนจริงไหม
  - กติกาโหลดเหมือน EditHistoryTimeline: preloaded ก่อนเปิด dialog · ทิ้งผลที่มาช้า
============================================================ -->
<template>
  <div>
    <div class="modal-sec mt cut-head">
      <span><span class="mdi mdi-calendar-check-outline"></span> {{ $t('courseUsage.title') }}</span>
      <span v-if="items.length" class="cut-filters">
        <button type="button" :class="['cut-chip', { on: !liveOnly }]" @click="liveOnly = false">{{ $t('courseUsage.filterAll') }} ({{ items.length }})</button>
        <button type="button" :class="['cut-chip', { on: liveOnly }]" @click="liveOnly = true">{{ $t('courseUsage.filterLive') }} ({{ liveCount }})</button>
      </span>
    </div>
    <div v-if="loading" class="cut-loading"><span v-if="loadingSlow">{{ $t('common.loading') }}</span></div>
    <template v-else>
      <div v-if="!shown.length" class="t-cap" style="padding:2px 0 4px">{{ $t('courseUsage.empty') }}</div>
      <div v-else class="cut-list">
        <div v-for="(e, i) in shown" :key="i" class="cut-entry">
          <span :class="['cut-ico', 'badge', TYPE_META[e.type].badge]"><span :class="['mdi', TYPE_META[e.type].icon]"></span></span>
          <div class="cut-body">
            <div>
              <b>{{ $t(TYPE_META[e.type].label) }}</b>
              <template v-if="e.type === 'event'"> · {{ e.eventname || '-' }}</template>
              · <template v-if="e.type === 'cancel'"><s>{{ when(e.classdate, e.classtime) }}</s></template>
              <template v-else-if="e.type === 'move'">{{ when(e.classdate, e.classtime) }} <span class="mdi mdi-arrow-right"></span> {{ when(e.toClassdate, e.toClasstime) }}</template>
              <template v-else>{{ when(e.classdate, e.classtime) }}</template>
              <template v-if="e.nickname"> · {{ e.nickname }}</template>
              <span v-if="e.free" class="badge badge-neutral cut-tag">{{ $t('courseUsage.free') }}</span>
            </div>
            <div class="t-cap">
              {{ e.actor || '-' }}<template v-if="roleKey(e.role)"> · {{ $t(roleKey(e.role)) }}</template> · {{ $formatDateTime(e.at) }}
              <template v-if="e.type === 'cancel' && e.bookedBy"> · {{ $t('courseUsage.bookedBy') }} {{ e.bookedBy }}</template>
            </div>
          </div>
        </div>
      </div>
      <div class="t-cap" style="margin-top:6px"><span class="mdi mdi-information-outline"></span> {{ $t('courseUsage.since') }}</div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { ROLE_LABELS } from './BookedByCell.vue';

const TYPE_META = {
  book: { label: 'courseUsage.book', icon: 'mdi-plus', badge: 'badge-success' },
  move: { label: 'courseUsage.move', icon: 'mdi-arrow-right', badge: 'badge-info' },
  cancel: { label: 'courseUsage.cancel', icon: 'mdi-close', badge: 'badge-error' },
  checkin: { label: 'courseUsage.checkin', icon: 'mdi-check', badge: 'badge-primary' },
  checkinUndo: { label: 'courseUsage.checkinUndo', icon: 'mdi-undo', badge: 'badge-neutral' },
  event: { label: 'courseUsage.event', icon: 'mdi-trophy-outline', badge: 'badge-warning' },
};
const SLOW_MS = 400;

export default {
  name: 'CourseUsageTimeline',
  props: {
    preloaded: { type: Array, default: null }, // fetchCourseUsage ใน utils/history.js
    refid: { type: String, default: null },    // courserefer
  },
  data() {
    return { loading: false, loadingSlow: false, items: [], liveOnly: false, TYPE_META };
  },
  computed: {
    // type ที่ไม่รู้จัก (backend เพิ่มในอนาคต) ถูกกรองทิ้ง ไม่ให้ TYPE_META[...] พังทั้ง dialog
    known() { return this.items.filter((e) => TYPE_META[e.type]); },
    shown() { return this.liveOnly ? this.known.filter((e) => e.live) : this.known; },
    liveCount() { return this.known.filter((e) => e.live).length; },
  },
  watch: {
    refid: {
      immediate: true,
      handler() {
        this.liveOnly = false;
        if (this.preloaded) { this.items = this.preloaded; return; }
        this.fetch();
      },
    },
  },
  beforeUnmount() { clearTimeout(this.slowTimer); },
  methods: {
    roleKey(role) { return role === null || role === undefined ? null : ROLE_LABELS[role] || null; },
    // classdate เป็นสตริง YYYY-MM-DD (DATE_FORMAT ฝั่ง SQL) — ตัดเองไม่ผ่าน moment กันวันเลื่อนตาม timezone เครื่อง
    when(date, time) {
      const d = typeof date === 'string' ? date.slice(0, 10).split('-').reverse().join('/') : '-';
      return time ? `${d} ${time}` : d;
    },
    async fetch() {
      const refid = this.refid;
      this.items = [];
      this.loading = false;
      this.loadingSlow = false;
      clearTimeout(this.slowTimer);
      if (!refid) return;
      this.loading = true;
      this.slowTimer = setTimeout(() => { this.loadingSlow = true; }, SLOW_MS);
      try {
        const r = await axios.get(this.baseURL + '/getCourseUsageTimeline/' + encodeURIComponent(refid),
          { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } });
        if (this.refid !== refid) return;
        if (r.data.success) this.items = r.data.events || [];
      } catch (err) { /* ล้มเงียบ — dialog แม่ต้องเปิดได้เสมอ */
      } finally {
        if (this.refid === refid) {
          clearTimeout(this.slowTimer);
          this.loading = false;
          this.loadingSlow = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.cut-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
.cut-filters { display: inline-flex; gap: 6px; }
.cut-chip { font-size: 12px; font-weight: 500; padding: 2px 10px; border-radius: var(--radius-pill); border: 1px solid var(--c-border, #e5e7eb);
  background: transparent; color: var(--c-text-muted); cursor: pointer; }
.cut-chip.on { background: var(--c-primary-soft); color: var(--c-primary-dark); border-color: var(--c-primary-light); }
.cut-loading { min-height: 34px; display: flex; align-items: center; font-size: 13px; color: var(--c-text-muted); }
.cut-list { display: flex; flex-direction: column; }
.cut-entry { display: flex; gap: 10px; padding: 7px 0; border-top: 1px solid var(--c-border, #eee); font-size: 13px; }
.cut-entry:first-child { border-top: none; }
.cut-ico { width: 24px; height: 24px; padding: 0; justify-content: center; flex: 0 0 24px; font-size: 14px; }
.cut-body { flex: 1; min-width: 0; color: var(--c-text-body); }
.cut-body b { font-weight: 600; }
.cut-tag { height: 18px; padding: 0 7px; font-size: 11px; margin-left: 4px; }
</style>
